// The innernet organism mark — the flat 6-petal swirl with the hollow centre, the
// same living "organism" the product renders as your growing memory. Used SPARINGLY
// and only where the wordmark would be too cramped (favicons, avatar, an MCP/app
// tile). The wordmark stays primary everywhere it fits.
//
// THE CELL HOLDS ITS STANCE (law_mark_stance). The mark is drawn as two parts:
// the petal silhouette in `.om-petals`, and the centre cell cut out of it by a
// mask that sits OUTSIDE that group. Anything that turns the mark (a CTA's hover,
// the landing pill) turns `.om-petals` only, so the petals revolve around a cell
// that never tilts. Never rotate the <svg> itself.
import { useId } from "react";
import { ORGANISM, BRAND } from "../../lib/brand/marks";

export type MarkTone = "current" | "solid" | "gradient";

/** the canonical path is one even-odd shape: the petal silhouette, then the
 *  centre cell as its hole. Split once so the two can move independently. */
const MARK_PARTS = (() => {
  const i = ORGANISM.path.search(/Z(?=\s*M)/);
  return i < 0
    ? { petals: ORGANISM.path, cell: "" }
    : { petals: ORGANISM.path.slice(0, i + 1), cell: ORGANISM.path.slice(i + 1) };
})();

export default function OrganismMark({
  size = "1em",
  tone = "current",
  title,
  className,
  style,
}: {
  size?: string | number;
  tone?: MarkTone;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const id = useId().replace(/[:]/g, "");
  const gradId = `om-grad-${id}`;
  const maskId = `om-hole-${id}`;
  const fill =
    tone === "gradient" ? `url(#${gradId})` : tone === "solid" ? BRAND.blue : "currentColor";
  const [, , vw, vh] = ORGANISM.viewBox.split(" ").map(Number);
  return (
    <svg
      viewBox={ORGANISM.viewBox}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      className={className}
      // visible overflow: a petal swinging past the tight Figma box is never sheared
      style={{ width: size, height: size, display: "inline-block", verticalAlign: "middle", fill, flexShrink: 0, overflow: "visible", ...style }}
    >
      {title ? <title>{title}</title> : null}
      <defs>
        {tone === "gradient" && (
          <linearGradient id={gradId} x1="199" y1="0" x2="199" y2={395.741} gradientUnits="userSpaceOnUse">
            <stop stopColor={BRAND.organismGrad[0]} />
            <stop offset="1" stopColor={BRAND.organismGrad[1]} />
          </linearGradient>
        )}
        {/* the still cell — its white field spans the whole rotation envelope */}
        <mask id={maskId}>
          <rect x={-vw} y={-vh} width={vw * 3} height={vh * 3} fill="#fff" />
          <path d={MARK_PARTS.cell} fill="#000" />
        </mask>
      </defs>
      <g mask={`url(#${maskId})`}>
        <g className="om-petals">
          <path d={MARK_PARTS.petals} />
        </g>
      </g>
    </svg>
  );
}
